import { z } from "zod";
import { metconFormatIdSchema } from "./sessions";

/**
 * THE SHAPE LIBRARY, ON THE WIRE (BUD-329).
 *
 * ═══ WHAT IT IS FOR ═══
 *
 * The shape picker (design frame `10b`) marks a shape unavailable BEFORE anything is drawn.
 * It draws structure tabs, a row per shape, and a stacked bar per row whose segments are the
 * shape's slots — one segment per slot, its width the slot's share of the piece. None of that
 * is a draw, so none of it may cost one: the client needs the library as DATA, and asking the
 * engine to attempt a session in order to find out what shapes exist is the arrangement this
 * endpoint exists to avoid.
 *
 * ═══ WHAT IS DELIBERATELY NOT HERE ═══
 *
 * `ref` — the benchmark each shape is drawn from ("Fran · Diane · Elizabeth"). Its own header
 * in `api/src/engine/library/shapes.ts` says it is for a human reading the library: no rule
 * reads it and no session prints it. Publishing it would invite a client to print "Fran" on a
 * card, and then the name of a workout nobody licensed us to name is a product feature.
 *
 * `sch` — the authored per-gear dose. The picker shows which shapes EXIST, not what any of
 * them would prescribe; the dose belongs to an offer, which is `sessions`' answer and arrives
 * with the gear the athlete actually picked.
 *
 * `want` — the functional pattern a slot insists on (`pull` on a ladder's second slot). It is
 * a constraint the DRAW resolves, not part of the signature `10b` renders, and adding it later
 * is additive. It is left out on the same terms as the dose: this endpoint publishes what the
 * library holds, and everything about whether a shape fits today is the constraint-evaluation
 * work that follows.
 *
 * `builtRest` — rest written into the piece, per gear. Same reason as the dose.
 *
 * PROSE, OF ANY KIND. `Structure.arranges` reads "a descending rep scheme over rounds" and
 * `MetconFormat.scoredBy` reads "total time". Neither goes on this wire, for the reason
 * `sessions.ts` already gives about the format registry: the app would either print English
 * into a German screen or key a translation off a sentence. The tab word for `ladder` is the
 * app's, keyed by the id, exactly as a reason code's sentence is.
 */

/**
 * ONE SLOT OF A SHAPE — what kind of movement goes in it, and how much of it.
 *
 * ⚠️ `vol` IS PUBLISHED RAW AND NOT AS A PERCENTAGE. `10b` draws the slots as a stacked bar,
 * so what it renders is each slot's SHARE of the total — but a share is a rendering, and the
 * division is one line on the client. Publishing pre-divided percentages would round, would
 * lose the number the library actually authored, and would put a layout decision in the
 * service; a second client wanting the numbers side by side rather than as a bar could not
 * get them back.
 */
export const shapeSlotSchema = z.object({
  /**
   * THE KINDS OF MOVEMENT THIS SLOT TAKES — `weightlifting` · `gymnastic` · `monostructural`,
   * the engine's modality registry, which `logs.ts` and `movements.ts` already carry under
   * this name. AN ARRAY, NOT A SINGLE VALUE (BUD-365): `engine/library/types.ts`'s `Slot.mod`
   * widened from one modality to a set, because most of the library's pins had nothing to
   * balance against — a death-by pinned to `gymnastic` alone excluded "death by snatches" for
   * no reason the shape itself gave. `10b`'s stacked bar reads every entry, not just the first.
   *
   * OPEN STRINGS, NOT AN ENUM, because the engine's own `ModalityId` is open: the registry
   * exists precisely so that the prototype's closed three-way world (`loaded ? 'W' :
   * monostructural ? 'M' : 'G'`) can gain a row. A closed enum here would delete content the
   * service is happy to serve, which is the rule `sessions.ts` states for every open alias.
   */
  mod: z.array(z.string()).min(1),
  /** The total volume this slot demands. A movement whose ceiling is lower cannot fill it. */
  vol: z.number(),
  /**
   * TRUE WHEN ONLY A REP-MEASURED MOVEMENT FITS; false lets the slot take a machine counted in
   * calories or metres. This is the `*` the design handoff marks a slot with — `*` is what a
   * client DRAWS when this is false, and the wire carries the fact rather than the glyph.
   *
   * 🔑 ALWAYS PRESENT, WHICH THE ENGINE'S FIELD IS NOT. `Slot.repsOnly` is optional there and
   * defaults to TRUE when absent, which is the one default that cannot survive a network hop:
   * an absent boolean reads as `undefined` on the other side, `undefined` is falsy, and the
   * client would draw a `*` on every slot in the library. The view writes the default out.
   */
  repsOnly: z.boolean(),
});

export type ShapeSlotDTO = z.infer<typeof shapeSlotSchema>;

/** One shape: what it is called, how its reps are arranged, how it may be run, and its slots. */
export const shapeSchema = z.object({
  /**
   * `LADDER-21`, `COUPLET-WG`, `SINGLE-HVY`. AN OPEN STRING for the reason `ShapeId` is an open
   * alias in the engine — the shape library is Tier B content a coach may extend without a
   * deploy, so the set of ids is not something either side of this wire can close.
   *
   * ⚠️ IT IS ALSO WHAT `10b` PRINTS AS THE ROW TITLE, verbatim. That is the design's decision,
   * not an accident of having nothing better: the ids are written to be read.
   */
  id: z.string(),
  /**
   * How the reps are ARRANGED — `ladder` · `rounds` · `chipper` · `singlet` · `escalating`.
   * These are `10b`'s tabs, and every one of them appears in `structures` below.
   *
   * OPEN HERE, exactly as `sessionBlockSchema.structure` is open: `library` owns the union.
   */
  structure: z.string(),
  /**
   * The formats this shape may legally be RUN and SCORED in. Closed, and reusing the enum
   * `sessions.ts` already declares rather than restating it — a format is Tier A, it changes
   * control flow, and it cannot arrive without a deploy.
   *
   * One shape runs as several sessions through this list: Cindy and Chelsea are the same round
   * — 5 pull-ups, 10 push-ups, 15 air squats — as a 20-minute AMRAP and as a 30-minute EMOM.
   */
  formats: z.array(metconFormatIdSchema),
  /** In authored order. The bar in `10b` is drawn left to right in this order. */
  slots: z.array(shapeSlotSchema),
});

export type ShapeDTO = z.infer<typeof shapeSchema>;

/**
 * A STRUCTURE, WHICH ON THIS WIRE IS AN ID AND NOTHING ELSE.
 *
 * WHY IT IS PUBLISHED AT ALL: a client that hardcodes `["ladder", "rounds", "chipper",
 * "singlet", "escalating"]` is a second copy of the library, and it is the copy that goes stale
 * silently — a structure added in the engine gets shapes the picker has no tab for, and nothing
 * anywhere says so.
 *
 * WHY IT CARRIES NO LABEL: see the note at the top of this file. The registry's own sentence is
 * a coach's English; the tab says "Ladder". The word is the app's, keyed by the id, and guarded
 * the way the reason codes are — every id has wording in every language, and no wording exists
 * for an id that is not published.
 *
 * AN OBJECT RATHER THAN A BARE STRING, so that a field can be added to it later without
 * breaking a build already on a phone. An array of strings has no additive next version.
 */
export const shapeStructureSchema = z.object({ id: z.string() });

export type ShapeStructureDTO = z.infer<typeof shapeStructureSchema>;

/**
 * THE WHOLE LIBRARY IN ONE ANSWER — the tabs and the rows, together.
 *
 * ONE REQUEST, NOT TWO, because `10b` cannot draw either half alone: the tabs filter the rows,
 * and a tab whose rows have not arrived is a control that does nothing. Two endpoints would
 * also make it possible to hold a structure list from one deploy beside a shape list from
 * another, which is the one inconsistency this screen cannot survive.
 */
export const shapeCatalogueSchema = z.object({
  /**
   * WHICH LIBRARY THIS IS. The same stamp an offer already carries in its provenance
   * (`libraryVersion`), so a client holding a cached catalogue can tell whether the session it
   * was just offered was drawn from the library it is showing.
   *
   * It is a DECLARED string, not a hash of the rows — the engine declares it so that nothing
   * has to run over the library to know what it is.
   */
  libraryVersion: z.string(),
  /** The picker's tabs, in the registry's own declaration order. */
  structures: z.array(shapeStructureSchema),
  /** Every shape the library holds, unconditionally — thirty as supplied. */
  shapes: z.array(shapeSchema),
});

export type ShapeCatalogueDTO = z.infer<typeof shapeCatalogueSchema>;
